/**
 * useTransaction — RPC dispatcher 훅 (vue-spring-fw 포팅).
 *
 * 【역할】
 * - 하나의 `transaction({...})` 호출로 /dataset/search 또는 /dataset/save 에 dispatch.
 * - inds(입력 DataSet) 직렬화 + outds(출력 DataSet) 자동 로드 + loading/에러 토스트 통합.
 *
 * 【주요 기능】
 * - transaction(config): Promise<TransactionResult> — 검색/저장 공용 진입점
 * - action 자동 판별: inds 에 변경 (C/U/D) 있으면 'save', 아니면 'search'
 * - outds 매핑: result.data[key] 가 배열이거나 {rows:[]} 구조면 해당 DataSet.load() 자동 호출
 * - 에러 처리: 401/403 은 조용히 통과 (axios interceptor 가 처리), 그 외는 message.error 토스트
 *
 * 【inds/outds 매핑 규칙】
 * - inds: 키 = 서버 DataSet 명 (예: `ds_search`, `ds_user`)
 *   - UseDataSetResult 인 경우 getChanges() 로 C/U/D 분리 + getVisibleRows() 로 전체 스냅샷 포함
 *   - 원시 객체/배열은 그대로 전달 (단건 파라미터 DS 용)
 * - outds: 키 = 서버 응답 data.<key>, 값 = UseDataSetResult 인스턴스
 *   - 응답이 배열이면 ds.load(arr), {rows:[...]} 이면 ds.load(obj.rows).
 *
 * 【사용 예시】
 * ```tsx
 * const { transaction } = useTransaction()
 * await transaction({
 *   id: 'searchUsers',
 *   service: 'user',          // serviceName = 'user/search'
 *   inds: { ds_search: { keyword } },
 *   outds: { ds_user: dsUser }
 * })
 * ```
 *
 * 【왜 이 패턴?】
 * - RPC 1-call 로 여러 DataSet 을 한 번에 왕복해야 하는 ts-spring 계열 기업 프런트의 통상 패턴.
 * - 개별 useQuery/useMutation 으로 분리하지 않고 단일 dispatcher 를 두어 페이지 코드 경량화.
 */
import { useCallback, useState } from 'react'
import http from './api/axios'
import i18n from '@/i18n'
import { useFrameworkContext } from './FrameworkProvider'
import type { UseDataSetResult } from './useDataSet'

/** transaction({...}) 호출 시 전달할 설정 객체. */
export interface TransactionConfig {
  /** 호출자 식별 ID — 로그/_caller 헤더에 사용 (페이지 내 고유 권장) */
  id: string
  /** 서비스명 — 'user' 또는 'user/search' 등. '/' 없으면 action 으로 자동 결합 */
  service: string
  /** action — 보통 'search' 또는 'save'. 생략 시 inds 변경 유무로 자동 판별 */
  action?: 'search' | 'save' | string
  /** 입력 DataSet (키=서버DS명, 값=UseDataSetResult 또는 원시 객체) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- 다양한 UseDataSetResult<T> 인스턴스를 한 Record 에 모으기 위한 공변 컨테이너
  inds?: Record<string, UseDataSetResult<any> | Record<string, unknown> | unknown[]>
  /** 출력 DataSet (서버응답 매핑) — 키=응답 data 의 키, 값=load() 받을 dataset */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- 다양한 UseDataSetResult<T> 인스턴스를 한 Record 에 모으기 위한 공변 컨테이너
  outds?: Record<string, UseDataSetResult<any>>
  /** 추가 params — ds_search 에 자동 병합되어 서버로 전달 */
  params?: Record<string, unknown>
  /** 동기 여부 (현재는 모두 Promise 기반 — 호환 차원의 옵션) */
  sync?: boolean
  /** 전역 로딩 스피너 사용 여부 (기본 true) — 백그라운드 호출 시 false */
  loading?: boolean
  /** 완료 콜백 — success 여부와 무관하게 호출됨 */
  callback?: (result: TransactionResult) => void
}

/** transaction() 결과 — reject 하지 않고 success=false 로 실패 신호. */
export interface TransactionResult {
  /** 서버 success 플래그 또는 자체 판단 (네트워크 에러 시 false) */
  success: boolean
  /** 서버 응답 data 본문 — outds 매핑 후에도 원본 보존 */
  data: Record<string, unknown>
  /** 에러 메시지 또는 서버 메시지 (성공 시 null) */
  message: string | null
}

/**
 * inds 직렬화 — UseDataSetResult 는 {rows, changes} 로 해체, 그 외 원시값은 그대로.
 * @returns datasets 페이로드 + hasChanges (action 자동 판별용 플래그).
 */
function serializeInds(inds?: TransactionConfig['inds']): {
  datasets: Record<string, unknown>
  hasChanges: boolean
} {
  const datasets: Record<string, unknown> = {}
  let hasChanges = false
  if (!inds) return { datasets, hasChanges }
  for (const [key, val] of Object.entries(inds)) {
    if (!val) {
      datasets[key] = val
      continue
    }
    // UseDataSetResult 판별
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- 런타임 duck-typing 으로 공통 인터페이스 검사
    const candidate = val as Partial<UseDataSetResult<any>>
    if (typeof candidate.getChanges === 'function') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic 행 타입을 통합 처리
      const ds = val as UseDataSetResult<any>
      const changes = ds.getChanges()
      if (changes.inserted.length || changes.updated.length || changes.deleted.length) {
        hasChanges = true
      }
      datasets[key] = {
        rows: ds.getVisibleRows().map((r) => ({ ...r.data, _state: r._state })),
        changes
      }
    } else {
      datasets[key] = val
    }
  }
  return { datasets, hasChanges }
}

/**
 * useTransaction 훅 — transaction dispatcher + 에러 상태 노출.
 * @returns { transaction, error } — 페이지 코드에서는 주로 transaction 만 사용.
 */
export function useTransaction() {
  const { pushLoading, popLoading, message } = useFrameworkContext()
  const [error, setError] = useState<string | null>(null)

  /**
   * RPC 실행 본체 — inds 직렬화 → POST /dataset/(search|save) → outds 자동 로드.
   * @param config - id/service/inds/outds/params/loading/callback 설정.
   * @returns { success, data, message } — 실패 시 success=false 이지만 reject 하지 않음 (호출부가 분기).
   */
  const transaction = useCallback(
    async (config: TransactionConfig): Promise<TransactionResult> => {
      const useLoading = config.loading !== false
      if (useLoading) pushLoading()
      setError(null)

      try {
        const { datasets, hasChanges } = serializeInds(config.inds)
        const action = config.action ?? (hasChanges ? 'save' : 'search')
        const isSave = action === 'save' || hasChanges
        const url = `/dataset/${isSave ? 'save' : 'search'}`
        const serviceName = config.service.includes('/')
          ? config.service
          : `${config.service}/${action}`
        const dsWithParams =
          config.params && Object.keys(config.params).length
            ? {
                ...datasets,
                ds_search: {
                  ...((datasets.ds_search as Record<string, unknown>) ?? {}),
                  ...config.params
                }
              }
            : datasets
        const body = {
          id: config.id,
          serviceName,
          _caller: config.id,
          datasets: dsWithParams
        }

        const res = await http.post(url, body)
        const result: TransactionResult = {
          success: res.data?.success ?? true,
          data: res.data?.data ?? res.data ?? {},
          message: res.data?.message ?? null
        }

        if (!result.success) {
          throw new Error(
            result.message ||
              (i18n.t('MSG_SERVER_ERROR', { defaultValue: '서버 오류가 발생했습니다.' }) as string)
          )
        }

        // outds 자동 로드
        if (config.outds && result.data) {
          for (const [key, ds] of Object.entries(config.outds)) {
            const payload = result.data[key]
            if (!payload) continue
            if (Array.isArray(payload)) ds.load(payload as Record<string, unknown>[])
            else if (
              typeof payload === 'object' &&
              Array.isArray((payload as { rows?: unknown }).rows)
            ) {
              ds.load((payload as { rows: Record<string, unknown>[] }).rows)
            }
          }
        }

        config.callback?.(result)
        return result
      } catch (err: unknown) {
        const errObj = err as {
          response?: { status?: number; data?: { message?: string } }
          message?: string
        }
        const status = errObj.response?.status
        if (status === 401 || status === 403) {
          return { success: false, data: {}, message: '' }
        }
        const netFallback = i18n.t('MSG_NETWORK_ERROR', {
          defaultValue: '서버 통신 오류'
        }) as string
        const msg = errObj.response?.data?.message || errObj.message || netFallback
        setError(msg)
        message.error(msg)
        return { success: false, data: {}, message: msg }
      } finally {
        if (useLoading) popLoading()
      }
    },
    [pushLoading, popLoading, message]
  )

  return { transaction, error }
}
